import { useState, useRef, useEffect, useLayoutEffect, forwardRef, Children, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

/*******************************************************
 * 파일명 : frontend2\src\components\bootstrap\forms\CustomSelect.js
 * 메뉴 : select 커스텀 재사용 컴포넌트 (기존 Select.js 대체)
 * 설명 : 기존 Select.js는 select + option 태그 사용으로 css 조작이 어려움 => 메타페이의 커스텀 셀렉트와 같이 디자인 하기 위해 만든 커스텀 컴포넌트
 *
 * 기존 Select.js와 동일하게 지원하는 것
 *  - list={[{ value, text | label }]}  방식
 *  - children으로 <option>을 직접 나열하는 방식 (children이 있으면 list보다 우선)
 *  - placeholder, disabled(전체 + 옵션 단위)
 *  - formik 연동: value / onChange / onBlur / onFocus / isValid / isTouched / invalidFeedback / validFeedback
 *  - forwardRef, className/style/name/aria-* 등 전달 (ref로 트리거 DOM 접근 가능)

 *******************************************************/

const CustomSelect = forwardRef(function CustomSelect(
	{
		id = null,
		name = null,
		className = null,
		wrapperClassName = null,
		children = null,
		placeholder = null,
		ariaDescribedby = null,
		ariaLabelledby = null,
		ariaLabel,
		list = null,
		disabled = false,
		value = undefined,
		isValid = false,
		isTouched = false,
		invalidFeedback = null,
		validFeedback = null,
		isValidMessage = true,
		onBlur = null,
		onChange = null,
		onFocus = null,
		maxWidth = null,
		showPlaceholderOnEmpty = false,
		style,
		...rest
	},
	ref,
) {
	const {t} = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [uncontrolledValue, setUncontrolledValue] = useState(undefined);
	const wrapperRef = useRef(null);
	const listRef = useRef(null);
	const [listPos, setListPos] = useState(null);
	const [listReady, setListReady] = useState(false);
	const currentValue = value !== undefined ? value : uncontrolledValue;

	// children이 있으면 children에서, 없으면 list에서 option 랜딩
	// value, text 형태로 통일하여 사용
	const optionsData = children ?
		Children.toArray(children)
			.filter((child) => isValidElement(child) && !child.props.hidden)
			.map((child) => ({
				value: child.props.value,
				text: child.props.children,
				disabled: child.props.disabled,
			}))
		: (list || []).map((item) => ({
			value: item.value,
			text: item.text ?? item.label,
			disabled: item.disabled,
		}));

	const handleSelect = (option) => {
		if(option.disabled) return;
		if(value === undefined) setUncontrolledValue(option.value);
		onChange?.({ target: { value: String(option.value) } });
		setIsOpen(false);
	};

	// list 배열에서 선택된 value와 일치하는 항목
	const selectedOption = showPlaceholderOnEmpty && (currentValue === '' || currentValue === null || currentValue === undefined)
		? null
		: optionsData?.find((opt) => String(opt.value) === String(currentValue));

	// 외부 클릭 시 닫기
	useEffect(() => {
		function handleClickOutside(e){
			const insideWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
			// option_list는 document.body로 포탈 렌더링되어 wrapperRef 밖에 있으므로 별도 확인 필요
			const insideList = listRef.current && listRef.current.contains(e.target);
			if(!insideWrapper && !insideList){
				if(isOpen){
					setIsOpen(false);
					onBlur?.({ target: { name, id, value: currentValue ?? '' } });
				}
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return() => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onBlur, name, id, currentValue]);

	useLayoutEffect(() => {
		if(!isOpen) {
			setListReady(false);
			return undefined;
		}
		const update = () => {
			const box = wrapperRef.current;
			if(!box) return;
			const rect = box.getBoundingClientRect();
			const below = window.innerHeight - rect.bottom - 8;
			const above = rect.top - 8;
			const openUp = below < 140 && above > below;
			setListPos({
				openUp,
				style: {
					position:'fixed',
					left:rect.left,
					width:rect.width,
					maxHeight:Math.min(280, Math.max(openUp ? above : below, 72)),
					...(openUp ? { bottom:window.innerHeight - rect.top } : { top:rect.bottom }),
				},
			});
		};
		update();
		// 최초 오픈 시 레이아웃이 아직 자리잡기 전에 측정되는 경우가 있어, 다음 프레임에 한 번 더 재측정.
		// 재측정 전까지는 안 보이게 해서 위치가 "점프"하는 게 보이지 않도록 함
		const raf = requestAnimationFrame(() => {
			update();
			setListReady(true);
		});
		// window 'resize'는 뷰포트 크기 변경만 감지함 — maxWidth='100%'처럼 부모 레이아웃에
		// 따라 너비가 늦게 확정되거나(비동기 데이터로 인한 리플로우 등) DPI 스케일링 환경에서
		// 트리거 자신의 박스 크기만 바뀌는 경우는 못 잡아서, wrapper 자체를 관찰
		const resizeObserver = new ResizeObserver(update);
		if(wrapperRef.current) resizeObserver.observe(wrapperRef.current);
		window.addEventListener('scroll', update, true);
		window.addEventListener('resize', update);
		return () => {
			cancelAnimationFrame(raf);
			resizeObserver.disconnect();
			window.removeEventListener('scroll', update, true);
			window.removeEventListener('resize', update);
		};
	}, [isOpen]);

	//////////formik에서 사용하는 옵션 연동 시 사용하기 위함
	const showInvalid = !isValid && isTouched && invalidFeedback;
	const showValid = !isValid && isTouched && !invalidFeedback;

	return (
		<div className={classNames('custom_select_box', wrapperClassName)} style={maxWidth ? { width: maxWidth, maxWidth } : undefined} ref={wrapperRef}>
			<button
				ref={ref}
				type='button'
				id={id}
				name={name}
				disabled={disabled}
				style={style}
				aria-label={ariaLabel}
				aria-describedby={ariaDescribedby}
				aria-labelledby={ariaLabelledby}
				className={classNames('btn_select', {
					active: isOpen,
					'is-invalid': showInvalid,
					'is-valid': showValid,
					up: isOpen && listPos?.openUp,
				}, className)}
				onClick={() => !disabled && setIsOpen(!isOpen)}
				onFocus={onFocus}
				onBlur={onBlur}
				{...rest}
			>
				<span className='txt'>{selectedOption ? selectedOption.text : placeholder ? placeholder : t('선택')}</span>
			</button>
			<span className='width_sizer' aria-hidden='true'>
				<span>{placeholder ? placeholder : t('선택')}</span>
				{optionsData?.map((opt) => (<span key={`sizer-${opt.value}`}>{opt.text}</span>))}
			</span>
			{isOpen && createPortal(
				<ul ref={listRef} className={classNames('option_list', { up: listPos?.openUp })} style={{ ...listPos?.style, visibility: listReady ? 'visible' : 'hidden' }}>
					{optionsData.length > 0 ?
						optionsData?.map((opt) => (
							<li key={opt.value}>
								<button
									type='button'
									disabled={opt.disabled}
									className={classNames({active: String(opt.value) === String(currentValue)})}
									onClick={() => handleSelect(opt)}
								>{opt.text}</button>
							</li>
						)) : <li style={{padding:'0 5px'}}>선택 옵션이 없습니다.</li>
					}
				</ul>,
				document.body,
			)}
			{isValidMessage && (showInvalid || showValid) && (
				<div className={showInvalid ? 'invalid-feedback d-block' : 'valid-feedback d-block'}>
					{showInvalid ? invalidFeedback : validFeedback}
				</div>
			)}
		</div>
	);
});

CustomSelect.propTypes = {
	id: PropTypes.string,
	name: PropTypes.string,
	className: PropTypes.string,
	wrapperClassName: PropTypes.string,
	children: PropTypes.node,
	ariaLabel: PropTypes.string.isRequired,
	placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	disabled: PropTypes.bool,
	ariaDescribedby: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	list: PropTypes.arrayOf(
		PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		disabled: PropTypes.bool,
		}),
	),
	isTouched: PropTypes.bool,
	isValid: PropTypes.bool,
	invalidFeedback: PropTypes.string,
	validFeedback: PropTypes.string,
	isValidMessage: PropTypes.bool,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	showPlaceholderOnEmpty: PropTypes.bool,
};

export default CustomSelect;
